import { useState } from "react";
import { Lock, LockOpen, Save, CheckCheck, Trash2 } from "lucide-react";
import { brl, pct } from "@/lib/format";
import { Badge, Button, Card, Field, Input, Segmented } from "@/components/kit";

export function SummaryCard({ conf, ed, isMgmt, mine }) {
  const s = conf.summary;
  const open = conf.status === "aberta";
  const done = mine.items.filter(ed.isCounted).length;
  const total = mine.items.length;
  return (
    <Card data-testid="conference-summary">
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <div className="caption" data-testid="counted-progress">{done} de {total} contados{mine.restricted ? " (seus setores)" : ""}</div>
          {isMgmt && <><div className="caption mt-3">CMV calculado · meta {pct(conf.cmv_target, 0)}</div><div className="h1 tnum mt-1" data-testid="conference-cmv-value">{brl(s.cmv)}</div></>}
        </div>
        <div className="flex flex-col items-end gap-2">
          <Badge tone={open ? "success" : "muted"}>{open ? "Aberta" : "Finalizada"}</Badge>
          {isMgmt && <span className="h2 tnum" style={{ color: s.pct > conf.cmv_target ? "var(--accent)" : "var(--success)" }} data-testid="conference-cmv-pct">{pct(s.pct)}</span>}
        </div>
      </div>
      <div className="bar-track mt-4"><div className="bar-fill" style={{ width: `${total ? (done / total) * 100 : 0}%` }} /></div>
      {ed.dirty && <p className="caption mt-2" style={{ color: "var(--accent)" }}>Valores alterados · salve para recalcular o CMV.</p>}
    </Card>
  );
}

const MODES = [{ value: "quantidade", label: "Por quantidade" }, { value: "notas", label: "Por nota fiscal" }];

export function SettingsCard({ conf, ed }) {
  const open = conf.status === "aberta";
  const notas = ed.mode === "notas";
  return (
    <Card className="flex flex-col gap-4">
      <Field label="Faturamento do período (R$)">
        <Input inputMode="decimal" disabled={!open} value={ed.revenue} onChange={(e) => ed.setRevenue(e.target.value)} data-testid="conference-revenue-edit-input" />
      </Field>
      <div className="flex flex-col gap-2">
        <span className="label">Como registrar as compras?</span>
        {open ? <Segmented testid="purchase-mode" value={ed.mode} onChange={ed.setMode} options={MODES} /> : <span className="text-sm">{notas ? "Por nota fiscal" : "Por quantidade"}</span>}
        <span className="caption">{notas ? "CMV = estoque inicial (R$) + valor das notas − estoque final (R$)" : "CMV = (inicial + compras − final) × custo unitário"}</span>
      </div>
    </Card>
  );
}

export function DeleteCard({ ed }) {
  const [confirm, setConfirm] = useState(false);
  return (
    <Card>
      {confirm ? (
        <div className="flex flex-col gap-3">
          <span className="label">Excluir esta conferência definitivamente?</span>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setConfirm(false)} data-testid="cancel-delete-conference">Cancelar</Button>
            <Button variant="danger" size="sm" loading={ed.del.isPending} onClick={() => ed.del.mutate()} data-testid="confirm-delete-conference">Excluir</Button>
          </div>
        </div>
      ) : (
        <Button variant="ghost" size="sm" icon={Trash2} onClick={() => setConfirm(true)} data-testid="delete-conference-button">Excluir conferência</Button>
      )}
    </Card>
  );
}

function PendingCard({ ed, onView }) {
  return (
    <div className="card shadow-lg" style={{ borderColor: "var(--accent)" }} data-testid="pending-confirm-card">
      <div className="label">{ed.pending.length} produto(s) ainda não contado(s)</div>
      <p className="caption mt-1 line-clamp-2">{ed.pending.map((p) => p.product_name).join(", ")}</p>
      <div className="flex gap-2 mt-3">
        <Button variant="secondary" size="sm" onClick={onView} data-testid="pending-view-button">Ver pendentes</Button>
        <Button size="sm" loading={ed.finalize.isPending} onClick={() => ed.finalize.mutate(true)} data-testid="pending-force-finalize-button">Finalizar mesmo assim</Button>
      </div>
    </div>
  );
}

export function ActionBar({ conf, ed, isMgmt, onViewPending }) {
  const open = conf.status === "aberta";
  return (
    <div className="sticky bottom-20 lg:bottom-4 mt-5 z-10 flex flex-col gap-3">
      {ed.pendingCard && <PendingCard ed={ed} onView={() => { onViewPending(); ed.setPendingCard(false); }} />}
      <div className="card flex flex-wrap gap-3 items-center justify-end shadow-lg" style={{ padding: 12 }}>
        {open ? (
          <>
            <Button variant="secondary" icon={Save} disabled={!ed.dirty} loading={ed.save.isPending} onClick={() => ed.save.mutate()} data-testid="save-conference-button">{ed.dirty ? "Salvar" : "Salvo"}</Button>
            {isMgmt && <Button icon={CheckCheck} loading={ed.finalize.isPending} onClick={() => ed.finalize.mutate(false)} data-testid="finalize-conference-button">Finalizar</Button>}
          </>
        ) : (
          <>
            <span className="caption flex items-center gap-1.5 mr-auto"><Lock size={14} /> Conferência finalizada · somente leitura</span>
            {isMgmt && <Button variant="secondary" icon={LockOpen} loading={ed.reopen.isPending} onClick={() => ed.reopen.mutate()} data-testid="reopen-conference-button">Reabrir para edição</Button>}
          </>
        )}
      </div>
    </div>
  );
}
